// src/lib/staffAuthHeaders.js
// MGMT-APPLICANTS-PAGES-STAFF-TOKEN-01. The staff Firebase ID token as an Authorization header
// for panel calls to mgmt-backend, plus the one readable line a page shows when the server
// refuses the staff sign-in.
//
// Why not the existing authHeader() (EnterpriseAgentAccessTab.js / useGtm.js): it throws when
// there is no current user, so the request is never sent. These pages must keep working exactly
// as before until the server gate lands (LA1b), so with no user this returns {} and the call
// goes out as it does today. It also waits for Firebase to restore the session first: App.js
// renders routes before onAuthStateChanged fires, so on a hard refresh of /applicants
// currentUser is still null when the page mounts.

import { getAuth } from 'firebase/auth';

const loggedAbsent = new Set();

function currentPage() {
    try {
        return (typeof window !== 'undefined' && window.location && window.location.pathname) || '';
    } catch (e) {
        return '';
    }
}

export async function staffAuthHeaders(getAuthFn = getAuth) {
    const auth = getAuthFn();
    if (auth && typeof auth.authStateReady === 'function') {
        await auth.authStateReady();
    }
    const user = auth && auth.currentUser;
    if (!user) {
        const page = currentPage();
        if (!loggedAbsent.has(page)) {
            loggedAbsent.add(page);
            console.warn(`[STAFF_TOKEN_ABSENT] ${JSON.stringify({ page })}`);
        }
        return {};
    }
    const token = await user.getIdToken();
    return { Authorization: `Bearer ${token}` };
}

export const STAFF_SIGN_IN_NOTICE = 'Your staff sign-in has expired. Sign in again.';

const AUTH_401_CODES = new Set(['STAFF_LOGIN_REQUIRED', 'missing_token', 'invalid_token']);

// The notice when the server refused the staff sign-in, otherwise null.
export function staffSignInNotice(error) {
    const res = error && error.response;
    if (!res) return null;
    const data = res.data || {};
    const code = data.code || data.error;
    if (res.status === 401 && AUTH_401_CODES.has(code)) return STAFF_SIGN_IN_NOTICE;
    if (res.status === 403 && code === 'access_denied') return STAFF_SIGN_IN_NOTICE;
    return null;
}

// Test-only: forget which pages already logged the absent line.
export function _resetStaffTokenAbsentLog() {
    loggedAbsent.clear();
}
